import type { ReactNode } from 'react';

/**
 * Drawn cover art for the template gallery. Every preview is a miniature of
 * the page the template creates — a table with rubles, a timeline with bars, a
 * checklist with ticks — drawn as a warm paper «blank», the way ready-made
 * forms look spread on a desk. The art is intentionally fixed-paper-coloured
 * (not theme-aware): a cover is a picture of a blank, not part of the chrome.
 */

const PAPER = '#F8F3EA';
const PAPER_LINE = '#E7DCC9';
const INK = '#4A4238';
const INK_SOFT = '#C9BCA7';
const INK_SOFTER = '#DED2BF';
const WHITE = '#FFFFFF';

type PreviewProps = { accent: string };

const Paper = ({
  accent,
  children,
}: PreviewProps & { children: ReactNode }) => (
  <svg
    viewBox="0 0 200 120"
    style={{ display: 'block', width: '100%', height: '100%' }}
    aria-hidden="true"
  >
    <rect
      x="0.5"
      y="0.5"
      width="199"
      height="119"
      rx="7"
      fill={PAPER}
      stroke="#E2D5BF"
    />
    {/* the blank's coloured heading strip — the signature of every card */}
    <rect x="12" y="11" width="40" height="8" rx="4" fill={accent} />
    <rect x="58" y="12" width="52" height="6" rx="3" fill={INK_SOFTER} />
    {children}
  </svg>
);

const line = (x: number, y: number, w: number, fill = INK_SOFT) => (
  <rect x={x} y={y} width={w} height={4} rx="2" fill={fill} />
);

const money = (x: number, y: number, text: string) => (
  <text
    x={x}
    y={y + 4}
    fill={INK}
    style={{ font: '600 6px system-ui, sans-serif' }}
  >
    {text}
  </text>
);

/** Table blank: смета (with rubles), бронирования (with status chips). */
export const PreviewTable = ({
  accent,
  money: showMoney,
  chips,
}: PreviewProps & { money?: boolean; chips?: boolean }) => (
  <Paper accent={accent}>
    <rect x="12" y="27" width="176" height="11" rx="2" fill="#EDE3D3" />
    {[46, 108, 150].map(x => (
      <g key={x}>{line(x + 4, 30, x === 150 ? 20 : 26, INK_SOFTER)}</g>
    ))}
    {[44, 60, 76, 92].map((y, row) => (
      <g key={y}>
        {line(16, y, 26, row % 2 ? INK_SOFT : INK)}
        {line(50, y, 22, INK_SOFTER)}
        {chips ? (
          <rect
            x="112"
            y={y - 1}
            width="30"
            height="7"
            rx="3.5"
            fill={accent}
            opacity={0.75 - row * 0.12}
          />
        ) : (
          line(112, y, 18, INK_SOFTER)
        )}
        {showMoney
          ? money(
              154,
              y - 2,
              ['120 000 ₽', '84 500 ₽', '46 000 ₽', '12 000 ₽'][row]
            )
          : line(154, y, 16, INK_SOFTER)}
        <line
          x1="12"
          y1={y + 11}
          x2="188"
          y2={y + 11}
          stroke={PAPER_LINE}
          strokeWidth="1"
        />
      </g>
    ))}
    <line
      x1="44"
      y1="27"
      x2="44"
      y2="108"
      stroke={PAPER_LINE}
      strokeWidth="1"
    />
    <line
      x1="106"
      y1="27"
      x2="106"
      y2="108"
      stroke={PAPER_LINE}
      strokeWidth="1"
    />
    <line
      x1="148"
      y1="27"
      x2="148"
      y2="108"
      stroke={PAPER_LINE}
      strokeWidth="1"
    />
  </Paper>
);

/** Timeline blank: staggered bars under a date ruler. */
export const PreviewGantt = ({
  accent,
  hour,
}: PreviewProps & { hour?: boolean }) => (
  <Paper accent={accent}>
    {/* ruler */}
    <line
      x1="12"
      y1="30"
      x2="188"
      y2="30"
      stroke={PAPER_LINE}
      strokeWidth="1"
    />
    {(hour
      ? ['09:00', '12:00', '15:00', '18:00']
      : ['1 нед', '3 нед', '5 нед', '7 нед']
    ).map((label, i) => (
      <text
        key={label}
        x={26 + i * 46}
        y="27"
        fill={INK_SOFT}
        style={{ font: '600 5.5px system-ui, sans-serif' }}
      >
        {label}
      </text>
    ))}
    {/* rows: label + bar */}
    {(hour
      ? [
          [0, 22],
          [8, 30],
          [26, 18],
          [44, 46],
          [74, 26],
          [92, 40],
        ]
      : [
          [0, 40],
          [18, 52],
          [46, 30],
          [60, 62],
          [96, 34],
          [112, 44],
        ]
    ).map(([x, w], i) => (
      <g key={i}>
        {line(14, 38 + i * 13, hour ? 16 : 12, INK_SOFTER)}
        <rect
          x={x + 36}
          y={37 + i * 13}
          width={w}
          height="7"
          rx="3.5"
          fill={accent}
          opacity={0.9 - i * 0.08}
        />
      </g>
    ))}
    {/* today marker */}
    <line
      x1={hour ? 84 : 110}
      y1="30"
      x2={hour ? 84 : 110}
      y2="112"
      stroke="#C4635A"
      strokeWidth="1.5"
      opacity="0.55"
    />
  </Paper>
);

/** Checklist blank: ticks and open boxes. */
export const PreviewChecklist = ({ accent }: PreviewProps) => (
  <Paper accent={accent}>
    {[30, 46, 62, 78, 94].map((y, i) => (
      <g key={y}>
        <rect
          x="14"
          y={y}
          width="9"
          height="9"
          rx="2.5"
          fill={i < 3 ? accent : 'transparent'}
          stroke={i < 3 ? accent : INK_SOFT}
          strokeWidth="1.2"
        />
        {i < 3 && (
          <path
            d={`M16 ${y + 4.6} l2 2.2 l4.2 -4.6`}
            stroke={WHITE}
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {line(29, y + 2.5, 88 - i * 6, i < 3 ? INK_SOFTER : INK_SOFT)}
        {line(150, y + 2.5, 20, INK_SOFTER)}
      </g>
    ))}
  </Paper>
);

/** Contacts blank: avatars, names, price tags. */
export const PreviewContacts = ({ accent }: PreviewProps) => (
  <Paper accent={accent}>
    {[32, 56, 80].map((y, i) => (
      <g key={y}>
        <circle
          cx="23"
          cy={y + 6}
          r="8"
          fill={accent}
          opacity={0.22 + i * 0.14}
        />
        <text
          x="23"
          y={y + 9}
          textAnchor="middle"
          fill={accent}
          style={{ font: '700 8px system-ui, sans-serif' }}
        >
          {['В', 'Ф', 'Д'][i]}
        </text>
        {line(38, y + 1, 52, INK)}
        {line(38, y + 9, 36, INK_SOFTER)}
        <rect
          x="128"
          y={y + 2}
          width="56"
          height="9"
          rx="4.5"
          fill={accent}
          opacity="0.16"
        />
        {money(136, y + 2, ['45 000 ₽', 'от 60 000 ₽', '18 000 ₽'][i])}
      </g>
    ))}
  </Paper>
);

/** Brief blank: an questionnaire — heading + prompt lines. */
export const PreviewDoc = ({ accent }: PreviewProps) => (
  <Paper accent={accent}>
    {[28, 58, 88].map((y, i) => (
      <g key={y}>
        <rect x="14" y={y} width="6" height="6" rx="1.5" fill={accent} />
        {line(25, y + 1, 58 + i * 8, INK)}
        {line(14, y + 12, 158 - i * 10, INK_SOFTER)}
        {line(14, y + 20, 132 + i * 6, INK_SOFTER)}
      </g>
    ))}
  </Paper>
);

/** Cabinet blank: hero band with sections and one embedded live view. */
export const PreviewCabinet = ({ accent }: PreviewProps) => (
  <Paper accent={accent}>
    <rect
      x="12"
      y="26"
      width="176"
      height="22"
      rx="4"
      fill={accent}
      opacity="0.9"
    />
    <rect
      x="20"
      y="33"
      width="64"
      height="4"
      rx="2"
      fill={WHITE}
      opacity="0.9"
    />
    <rect
      x="20"
      y="40"
      width="42"
      height="3"
      rx="1.5"
      fill={WHITE}
      opacity="0.6"
    />
    {[56, 74].map(y => (
      <g key={y}>
        {line(12, y, 44, INK)}
        {line(12, y + 10, 120, INK_SOFTER)}
      </g>
    ))}
    {/* the embedded budget view */}
    <rect
      x="12"
      y="92"
      width="176"
      height="18"
      rx="3"
      fill={WHITE}
      stroke={accent}
      strokeWidth="1"
    />
    <rect
      x="17"
      y="96"
      width="30"
      height="4"
      rx="2"
      fill={accent}
      opacity="0.7"
    />
    {line(17, 103, 60, INK_SOFTER)}
    {money(146, 98, '120 000 ₽')}
  </Paper>
);

/** Board blank: three kanban columns with cards (проекты и задачи). */
export const PreviewBoard = ({ accent }: PreviewProps) => (
  <Paper accent={accent}>
    {[12, 74, 136].map((x, col) => (
      <g key={x}>
        <rect x={x} y="27" width="52" height="82" rx="4" fill="#EFE7D8" />
        <rect
          x={x + 5}
          y="31"
          width={18 + col * 4}
          height="5"
          rx="2.5"
          fill={INK_SOFT}
        />
        {[42, 58, 74, 90].map((y, row) =>
          row < 3 + (col % 2) ? (
            <g key={y}>
              <rect
                x={x + 5}
                y={y}
                width="42"
                height="12"
                rx="3"
                fill={WHITE}
                stroke="#E2D5BF"
              />
              <rect
                x={x + 8}
                y={y + 3}
                width={col === 1 ? 20 : 28}
                height="3"
                rx="1.5"
                fill={INK_SOFT}
              />
              <rect
                x={x + 8}
                y={y + 8}
                width="10"
                height="2.5"
                rx="1"
                fill={accent}
                opacity="0.8"
              />
            </g>
          ) : null
        )}
      </g>
    ))}
  </Paper>
);

/** Habits blank: a week grid of ticks (трекер привычек). */
export const PreviewHabits = ({ accent }: PreviewProps) => {
  const done = [
    [1, 1, 1, 0, 1, 1, 0],
    [1, 0, 1, 1, 1, 0, 0],
    [1, 1, 1, 1, 0, 1, 1],
    [0, 1, 1, 1, 1, 1, 0],
  ];
  return (
    <Paper accent={accent}>
      {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d, i) => (
        <text
          key={d}
          x={44 + i * 21}
          y="33"
          textAnchor="middle"
          fill={INK_SOFT}
          style={{ font: '600 5.5px system-ui, sans-serif' }}
        >
          {d}
        </text>
      ))}
      {done.map((week, row) => (
        <g key={row}>
          {line(12, 43 + row * 19, 22, INK_SOFT)}
          {week.map((on, col) => (
            <rect
              key={col}
              x={35 + col * 21}
              y={39 + row * 19}
              width="13"
              height="13"
              rx="3"
              fill={on ? accent : 'transparent'}
              opacity={on ? 0.75 - col * 0.04 : 1}
              stroke={on ? accent : INK_SOFTER}
              strokeWidth="1"
            />
          ))}
        </g>
      ))}
    </Paper>
  );
};

/** Goals blank: progress bars with percentages (цели и OKR). */
export const PreviewGoals = ({ accent }: PreviewProps) => (
  <Paper accent={accent}>
    {[
      ['20 свадеб', 72],
      ['Сайт студии', 45],
      ['Нанять ассистента', 20],
    ].map(([, pct], i) => (
      <g key={i}>
        {line(12, 33 + i * 26, 70, INK)}
        {money(160, 30 + i * 26, `${pct}%`)}
        <rect
          x="12"
          y={41 + i * 26}
          width="176"
          height="8"
          rx="4"
          fill="#EDE3D3"
        />
        <rect
          x="12"
          y={41 + i * 26}
          width={Math.round((176 * Number(pct)) / 100)}
          height="8"
          rx="4"
          fill={accent}
          opacity={0.85 - i * 0.12}
        />
      </g>
    ))}
  </Paper>
);
